import { SubAPI } from "./SubAPI";

export class Files extends SubAPI {
    // upload puts the bytes straight into the bucket with the pre-signed url the
    // server hands back, so they never travel through the server itself
    async upload(props: { file: File }) {
        const created = await this.create({
            name: props.file.name,
            contentType: props.file.type,
            size: props.file.size,
        });

        const response = await fetch(created.uploadUrl, {
            method: "PUT",
            body: props.file,
            headers: { "Content-Type": props.file.type || "application/octet-stream" },
        });

        if (!response.ok) throw new Error("Couldn't upload " + props.file.name);

        return this.markUploaded({ id: created.file.id });
    }

    create(props: { name: string; contentType: string; size: number }) {
        return this.fetcher.post<Upload>("/api/file", props);
    }

    // markUploaded closes out an upload; until then the file is attached to nothing
    markUploaded(props: { id: number }) {
        return this.fetcher.post<FileRef>("/api/file/uploaded", props);
    }

    // downloadUrl is opened by the browser, which the server redirects to the bucket.
    // inline asks for the file to be shown rather than saved.
    downloadUrl(props: { id: number; inline?: boolean }) {
        return "/api/file/download?id=" + props.id + (props.inline ? "&inline=true" : "");
    }
}

// canPreview says whether the browser can show the file itself, so it's worth opening
// in a tab rather than downloading.
export function canPreview(file: { contentType: string }) {
    return file.contentType === "application/pdf" || file.contentType.startsWith("image/");
}

export type FileRef = {
    id: number;
    name: string;
    contentType: string;
    size: number;

    // null until the bytes have been confirmed against the bucket
    uploadedAt: string | null;

    archived: boolean;
    createdAt: string;
    createdBy: number;
    updatedAt: string;
}

// Upload is a file that has a row but no bytes yet
export type Upload = {
    file: FileRef;

    // uploadUrl takes the bytes with a PUT, and expires
    uploadUrl: string;
}
